import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import WidthFullIcon from "@mui/icons-material/WidthFull";
import WidthNormalIcon from "@mui/icons-material/WidthNormal";
import {
  PERMISSIONS,
  PQRSD_STATUS,
  PQRSD_TYPES,
  PQRSD_PRIORITY_LABELS,
  PQRSD_PRIORITY_COLORS,
} from "../../constants";
import TopScrollbar from "../generic/TopScrollbar";
import ColumnResizer from "../generic/ColumnResizer";
import useColumnWidths from "../generic/useColumnWidths";
import { fetchPqrsdPage } from "../../services/backend/PqrsdService";
import { listPqrsdGroups } from "../../services/backend/PqrsdConfigService";
import {
  appendPqrsdList,
  setPqrsdList,
  setPqrsdLoading,
  setPqrsdFilters,
} from "../../redux/slices/pqrsdSlice";

import { pqrsdText } from "./pqrsdLabel";
const PAGE_SIZE = 25;
// Each scroll-triggered burst of background auto-cascade pulls at most this many
// rows before pausing, so a broad search ("a") never quietly reads the whole
// collection. Scrolling near the end raises the cap by one more batch.
const CASCADE_BATCH = 50;

const STATUS_COLOR = {
  [PQRSD_STATUS.RADICADO]: "info",
  [PQRSD_STATUS.ASIGNADO]: "info",
  [PQRSD_STATUS.EN_TRAMITE]: "warning",
  [PQRSD_STATUS.RESPONDIDO]: "success",
  [PQRSD_STATUS.CERRADO]: "default",
  [PQRSD_STATUS.DEVUELTO]: "warning",
  [PQRSD_STATUS.REABIERTO]: "info",
  [PQRSD_STATUS.PENDIENTE_INFO]: "secondary",
  [PQRSD_STATUS.DESISTIDO]: "error",
  [PQRSD_STATUS.EN_RECURSO]: "warning",
  [PQRSD_STATUS.ANULADO]: "error",
};

const STATUS_LABELS = {
  [PQRSD_STATUS.RADICADO]: "Radicado",
  [PQRSD_STATUS.ASIGNADO]: "Asignado",
  [PQRSD_STATUS.EN_TRAMITE]: "En trámite",
  [PQRSD_STATUS.RESPONDIDO]: "Respondido",
  [PQRSD_STATUS.CERRADO]: "Cerrado",
  [PQRSD_STATUS.DEVUELTO]: "Devuelta",
  [PQRSD_STATUS.REABIERTO]: "Reabierta",
  [PQRSD_STATUS.PENDIENTE_INFO]: "Pendiente de información",
  [PQRSD_STATUS.DESISTIDO]: "Desistida",
  [PQRSD_STATUS.EN_RECURSO]: "En recurso de reposición",
  [PQRSD_STATUS.ANULADO]: "Anulada",
};

function formatDate(v) {
  if (!v) return "—";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
}

const MS_PER_DAY = 86400000;

// Whole calendar days (weekends/holidays included) between today and the SLA
// deadline. Both ends are normalized to local midnight so a same-day deadline
// reads as 0 ("Hoy"). Returns null when there's no usable deadline.
function calendarDaysLeft(deadline) {
  if (!deadline) return null;
  const d = deadline instanceof Date ? deadline : new Date(deadline);
  if (isNaN(d.getTime())) return null;
  const startToday = new Date();
  startToday.setHours(0, 0, 0, 0);
  const startDeadline = new Date(d);
  startDeadline.setHours(0, 0, 0, 0);
  return Math.round((startDeadline - startToday) / MS_PER_DAY);
}

// Terminal statuses: the SLA clock no longer runs, so we don't show a countdown
// and never flag them as urgent.
const TERMINAL_STATUS = new Set([
  PQRSD_STATUS.RESPONDIDO,
  PQRSD_STATUS.CERRADO,
  PQRSD_STATUS.DESISTIDO,
  // Not closed, but the term only comes back if the recurso is granted.
  PQRSD_STATUS.EN_RECURSO,
  PQRSD_STATUS.ANULADO,
]);

// Two page widths, because the two are wanted for different reasons.
//
// The default lines the list up with the Header's own container (maxWidth="xl"
// and its gutters, Header.js:108), so the title, the filters and the table all
// start where the logo does — a table that begins 20px left of everything else
// on the page reads as a mistake.
//
// Widescreen throws that away for people who would rather have the pixels: the
// gutters go to almost nothing and four more columns come with it. It is a
// choice, not a breakpoint, so it is remembered per browser.
const PAGE_SX = { py: { xs: 2, md: 4 } };
const PAGE_WIDE_SX = {
  py: { xs: 2, md: 4 },
  px: { xs: 1, sm: 2, md: 3 },
  maxWidth: 2200,
  mx: "auto",
};
const WIDE_STORAGE_KEY = "pqrsd.list.wideScreen.v1";

const readWide = () => {
  try {
    return window.localStorage.getItem(WIDE_STORAGE_KEY) === "1";
  } catch {
    // Private windows throw on access; the default width is the safe answer.
    return false;
  }
};

// Every column measured against the width it actually needs (radicado 174px,
// solicitante 204, estado 187, and so on): all ten of them come to ~1750px, so
// they only all fit on a genuinely wide monitor. Hence the tiers below, set so
// that each one fits the screens it applies to instead of promising ten columns
// at 1200px and scrolling anyway.
//
// Narrow screens keep the four that answer "what is this and how late is it" —
// radicado, asunto, días, estado. Head and body cells read the same entry, so
// they can never drift apart.
const WIDE = "@media (min-width:1700px)";
const HIDDEN = { display: "none" };
const WIDE_ONLY = { display: "none", [WIDE]: { display: "table-cell" } };

// Which columns fit which page width. Boxed in the header's container the table
// has at most 1488px to work with, so the four widest columns simply do not fit
// there and are the widescreen toggle's reward; the rest come in by viewport.
const columnSx = (wide) => ({
  // Solicitante is 204px of name and SLA another 87: measured against the
  // widths below, letting them in a tier earlier is what pushed a tablet into
  // sideways scrolling.
  solicitor: { display: { xs: "none", md: "table-cell" } },
  sla: { display: { xs: "none", lg: "table-cell" } },
  dependency: { display: { xs: "none", xl: "table-cell" } },
  // Only on a wide page, and only on a monitor with the room for it.
  type: wide ? WIDE_ONLY : HIDDEN,
  priority: wide ? WIDE_ONLY : HIDDEN,
  received: wide ? WIDE_ONLY : HIDDEN,
});

// Asunto is the elastic one: it absorbs the leftover width at each tier and
// truncates rather than pushing the table wider than the screen.
const SUBJECT_SX = {
  maxWidth: { xs: 140, sm: 200, md: 220, lg: 260 },
  [WIDE]: { maxWidth: 420 },
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// A header cell with a grab strip on its right edge. `width` is whatever the
// user dragged it to; without one the column sizes itself as before.
function HeadCell({ columnKey, sx, width, resizing, onStart, onReset, children }) {
  return (
    <TableCell
      data-column={columnKey}
      sx={{
        position: "relative",
        // Table cells size content-box by default, so a 163px column would
        // render 183 wide and the whole table would jump on the first drag.
        ...(width
          ? { width, minWidth: width, maxWidth: width, boxSizing: "border-box" }
          : null),
        ...sx,
      }}
    >
      {children}
      <ColumnResizer
        active={resizing}
        onStart={(e) => onStart(columnKey, e)}
        onReset={() => onReset(columnKey)}
      />
    </TableCell>
  );
}

// `scope` controls which PQRSDs the list shows:
//  - "all": every PQRSD (requires PQRSD.LIST — supervisor view).
//  - "mine": only PQRSDs whose assignee == current user (operator view).
//  - "triage": PQRSDs the AI couldn't classify (aiStatus needs_review/failed).
//  - "group": PQRSDs the AI routed to a group the current user belongs to.
export default function PqrsdAdminList({ scope = "all" } = {}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.value);
  const { list, loading, filters } = useSelector((state) => state.pqrsd);
  const [search, setSearch] = useState("");
  const [urgentOnly, setUrgentOnly] = useState(false);
  // For "group" scope: the groups the current user is a member of. We keep the
  // whole doc (not just the id) so the header can name the dependency.
  // null = not yet resolved; [] = resolved and the user is in no group.
  const [myGroups, setMyGroups] = useState(scope === "group" ? null : []);
  // Ids for the Firestore "in" filter. Memoized because this feeds loadPage's
  // dependency array — a fresh array each render would re-fetch in a loop.
  const myGroupIds = useMemo(() => (myGroups ? myGroups.map((g) => g.id) : null), [myGroups]);
  // Soft cap for the background auto-cascade. Starts at one batch and grows by a
  // batch each time the user scrolls near the end, so broad searches fetch in
  // throttled bursts instead of draining the collection at once.
  const [cascadeCap, setCascadeCap] = useState(CASCADE_BATCH);
  // Cascade mode = the list auto-pulls pages in the background (search typing or
  // the urgency sort, both of which need to see beyond the first page).
  const cascadeMode = !!search || urgentOnly;

  const perms = user?.permissions || [];
  const canSeeAll = perms.includes(PERMISSIONS.PQRSD.LIST);
  const canSeeMine = perms.includes(PERMISSIONS.PQRSD.MINE) || canSeeAll;
  // "all" + "triage" are supervisor views; "mine" + "group" are operator views.
  const allowed = scope === "mine" || scope === "group" ? canSeeMine : canSeeAll;

  // For "mine", we force-filter on the current user via array-contains so a
  // PQRSD assigned to several operarios surfaces for each of them. The
  // supervisor list keeps the single-assignee equality filter.
  const assigneeContains = scope === "mine" ? user?.userId : null;
  const effectiveAssignee = scope === "mine" || scope === "group" ? null : filters.assignee;
  const needsReview = scope === "triage";
  // The group list already names its dependency in the header, so the column
  // would just repeat it there.
  const showDependency = scope !== "group";
  const columnCount = showDependency ? 10 : 9;

  // Resolve the current user's group memberships for the "group" scope.
  useEffect(() => {
    if (scope !== "group" || !user?.userId) return;
    (async () => {
      const groups = await listPqrsdGroups();
      setMyGroups(
        groups.filter((g) =>
          (Array.isArray(g.memberIds) && g.memberIds.includes(user.userId)) ||
          (Array.isArray(g.supervisorIds) && g.supervisorIds.includes(user.userId))
        )
      );
    })();
  }, [scope, user?.userId]);

  // Pagination state — kept local because Firestore DocumentSnapshot cursors
  // are non-serializable and only matter while this view is mounted.
  const cursorRef = useRef(null);
  const inFlightRef = useRef(false);
  // Bumped on every reset so any in-flight fetch from a prior filter generation
  // can detect that its result is stale and bail out before mutating state.
  const generationRef = useRef(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  // The element that actually scrolls sideways, mirrored by TopScrollbar.
  const tableScrollRef = useRef(null);
  // Drag-resized column widths, remembered per browser. Shared by every scope:
  // it is the same table, and someone who widened Asunto meant it everywhere.
  const {
    widths: columnWidths,
    startResize,
    resetColumn,
    resetAll: resetColumnWidths,
    dragging: resizing,
    hasWidths: hasColumnWidths,
  } = useColumnWidths("pqrsd.list.columnWidths.v1");
  const headProps = { resizing, onStart: startResize, onReset: resetColumn };
  // Header-aligned by default; widescreen is opt-in and remembered.
  const [wideScreen, setWideScreen] = useState(readWide);
  const COLUMN_AT = useMemo(() => columnSx(wideScreen), [wideScreen]);
  const pageProps = wideScreen
    ? { maxWidth: false, sx: PAGE_WIDE_SX }
    : { maxWidth: "xl", sx: PAGE_SX };

  const toggleWideScreen = () => {
    setWideScreen((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(WIDE_STORAGE_KEY, next ? "1" : "0");
      } catch {
        // Remembering it is a convenience, not a reason to fail the click.
      }
      return next;
    });
  };

  // Convert the yyyy-mm-dd strings stored in filters into JS Date bounds. `from`
  // is start-of-day, `to` is end-of-day so a single-day filter naturally covers
  // the entire calendar day.
  const dateFrom = useMemo(() => {
    if (!filters.dateFrom) return null;
    const d = new Date(`${filters.dateFrom}T00:00:00`);
    return isNaN(d.getTime()) ? null : d;
  }, [filters.dateFrom]);
  const dateTo = useMemo(() => {
    if (!filters.dateTo) return null;
    const d = new Date(`${filters.dateTo}T23:59:59.999`);
    return isNaN(d.getTime()) ? null : d;
  }, [filters.dateTo]);

  const loadPage = useCallback(
    async (reset) => {
      if (!reset && inFlightRef.current) return;
      if (reset) {
        generationRef.current += 1;
        cursorRef.current = null;
        setHasMore(true);
        setCascadeCap(CASCADE_BATCH);
        dispatch(setPqrsdLoading(true));
      } else {
        setLoadingMore(true);
      }
      const myGen = generationRef.current;
      inFlightRef.current = true;
      try {
        const { items, lastDoc, hasMore: more } = await fetchPqrsdPage({
          status: filters.status,
          assignee: effectiveAssignee,
          assigneeContains,
          groupIds: scope === "group" ? (myGroupIds || []) : undefined,
          needsReview,
          dateFrom,
          dateTo,
          cursor: cursorRef.current,
          pageSize: PAGE_SIZE,
        });
        if (myGen !== generationRef.current) return;
        cursorRef.current = lastDoc;
        setHasMore(more);
        if (reset) dispatch(setPqrsdList(items));
        else dispatch(appendPqrsdList(items));
      } finally {
        if (myGen === generationRef.current) {
          inFlightRef.current = false;
          setLoadingMore(false);
          if (reset) dispatch(setPqrsdLoading(false));
        }
      }
    },
    [dispatch, filters.status, effectiveAssignee, assigneeContains, scope, myGroupIds, needsReview, dateFrom, dateTo]
  );

  // First load + reset on filter / scope changes.
  useEffect(() => {
    if (!allowed) {
      navigate("/home");
      return;
    }
    if (scope === "mine" && !user?.userId) return; // wait for user to hydrate
    if (scope === "group" && myGroupIds === null) return; // wait for groups to resolve
    if (scope === "group" && myGroupIds.length === 0) {
      // User belongs to no group → nothing to show, skip the (invalid) "in" query.
      dispatch(setPqrsdList([]));
      dispatch(setPqrsdLoading(false));
      setHasMore(false);
      return;
    }
    loadPage(true);
  }, [allowed, navigate, scope, user?.userId, myGroupIds, loadPage, dispatch]);

  // Infinite-scroll sentinel: load the next page when it enters the viewport.
  // In cascade mode the cascade itself does the fetching, so a scroll near the
  // end instead raises the soft cap by one batch to resume it; otherwise we load
  // the next page directly.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || !hasMore || inFlightRef.current) return;
        if (cascadeMode) {
          // Resume the paused cascade for one more batch.
          if (list.length >= cascadeCap) {
            setCascadeCap(list.length + CASCADE_BATCH);
          }
        } else {
          loadPage(false);
        }
      },
      { rootMargin: "200px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadPage, list.length, cascadeMode, cascadeCap]);

  // Auto-cascade while the search box has text (or urgency sort is on): keep
  // pulling pages until we run out, hit the current soft cap, or the user clears
  // the search. Matches surface as each chunk lands because Redux append
  // re-renders the table.
  const cascading = cascadeMode && hasMore && list.length < cascadeCap;
  useEffect(() => {
    if (!cascading) return;
    if (inFlightRef.current) return;
    loadPage(false);
  }, [cascading, list.length, loadingMore, loadPage]);

  // Each new search term / urgency toggle re-arms the soft cap at one batch, so
  // a fresh query always halts after the first burst rather than inheriting a
  // cap grown large by an earlier search.
  useEffect(() => {
    setCascadeCap(CASCADE_BATCH);
  }, [search, urgentOnly]);

  // Days-left used as the urgency sort key. Terminal / no-deadline rows have no
  // running clock, so they sort to the bottom.
  const urgencyKey = useCallback((p) => {
    if (TERMINAL_STATUS.has(p.pqrsd?.status)) return Infinity;
    // A requerimiento de información suspends the entity's term (CPACA art. 17):
    // nothing is running down, so it can't be urgent.
    if (p.pqrsd?.slaPaused) return Infinity;
    const daysLeft = calendarDaysLeft(p.pqrsd?.slaDeadline);
    return daysLeft == null ? Infinity : daysLeft;
  }, []);

  const filtered = useMemo(() => {
    let result = list;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter((p) =>
        [p.filedId, p.subject, p.pqrsd?.email, p.pqrsd?.solicitor?.name, p.pqrsd?.solicitor?.surname, p.pqrsd?.groupName]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(s))
      );
    }
    if (urgentOnly) {
      // Keep every PQRSD but reorder ascending by days left, so the most urgent
      // (overdue → negative) sit at the top and the ones with more time
      // remaining fall to the bottom.
      result = result.slice().sort((a, b) => urgencyKey(a) - urgencyKey(b));
    }
    return result;
  }, [list, search, urgentOnly, urgencyKey]);

  // Status line under the search field. Tells the user what's happening so the
  // background auto-cascade never feels like a frozen UI.
  const statusMessage = useMemo(() => {
    if (search) {
      if (cascading) {
        return `Buscando en todos los resultados… ${list.length} revisados · ${filtered.length} coincidencias`;
      }
      if (!hasMore) {
        return `Búsqueda completa: ${filtered.length} coincidencias en ${list.length} resultados.`;
      }
      if (list.length >= cascadeCap) {
        return `Mostrando los primeros ${list.length} resultados. Desplázate para revisar más.`;
      }
    }
    if (urgentOnly) {
      if (cascading) {
        return `Ordenando por urgencia… ${list.length} cargados`;
      }
      if (!hasMore) {
        return `Ordenado por urgencia: vencidas y próximas a vencer primero (${filtered.length}).`;
      }
      if (list.length >= cascadeCap) {
        return `Ordenando los primeros ${list.length} resultados por urgencia. Desplázate para cargar más.`;
      }
    }
    return "Filtra los resultados cargados. Desplázate para cargar más.";
  }, [search, urgentOnly, cascading, hasMore, list.length, filtered.length, cascadeCap]);

  const title =
    {
      mine: pqrsdText("Mis PQRSDs"),
      triage: "Triage — sin clasificar",
      group: pqrsdText("PQRSD de mi dependencia"),
    }[scope] || pqrsdText("Gestionar PQRSDs");

  // "Mi dependencia" while the memberships are still resolving: a spinner rather
  // than the toolbar over an empty table, which would read as "no results".
  if (scope === "group" && myGroups === null) {
    return (
      <Container {...pageProps}>
        <Typography variant="h4" sx={{ mb: 3 }}>{title}</Typography>
        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // The user belongs to no dependency, so this list can never hold anything.
  // Say so plainly instead of rendering filters over a permanently empty table.
  if (scope === "group" && myGroups.length === 0) {
    return (
      <Container {...pageProps}>
        <Typography variant="h4" sx={{ mb: 3 }}>{title}</Typography>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            No perteneces a ninguna dependencia.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pqrsdText("Pide a un administrador que te agregue a una en la configuración de PQRSD.")}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container {...pageProps}>
      <Typography variant="h4" sx={{ mb: 3 }}>{title}</Typography>
      {scope === "mine" && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {pqrsdText("Solo se muestran las PQRSDs asignadas a tu usuario.")}
        </Typography>
      )}
      {scope === "triage" && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {pqrsdText("PQRSD que la IA no pudo clasificar con confianza o donde falló el análisis. Revísalas y asígnalas manualmente.")}
        </Typography>
      )}
      {scope === "group" && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {pqrsdText("PQRSD enrutadas por la IA a tu dependencia. Definan quién la atiende.")}
          </Typography>
          {/* Name the dependency the list is showing — a user can belong to more
              than one, which the "in" query already supports. */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {myGroups.map((g) => (
              <Chip key={g.id} size="small" color="primary" label={`Dependencia: ${g.name}`} />
            ))}
          </Stack>
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          flexWrap="wrap"
          useFlexGap
        >
          <TextField
            size="small"
            label="Buscar radicado, asunto, solicitante"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              label="Estado"
              value={filters.status || ""}
              onChange={(e) =>
                dispatch(setPqrsdFilters({ status: e.target.value || null }))
              }
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(STATUS_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label="Desde"
            value={filters.dateFrom || ""}
            onChange={(e) =>
              dispatch(setPqrsdFilters({ dateFrom: e.target.value || null }))
            }
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            size="small"
            type="date"
            label="Hasta"
            value={filters.dateTo || ""}
            onChange={(e) =>
              dispatch(setPqrsdFilters({ dateTo: e.target.value || null }))
            }
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          {(filters.dateFrom || filters.dateTo) && (
            <Tooltip title="Limpiar fechas">
              <span>
                <IconButton
                  size="small"
                  onClick={() =>
                    dispatch(setPqrsdFilters({ dateFrom: null, dateTo: null }))
                  }
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ ml: { sm: "auto" } }}
          >
            <Tooltip title="Ordenar por urgencia (vencidas y próximas a vencer primero)">
              <ToggleButton
                size="small"
                value="urgent"
                color="warning"
                selected={urgentOnly}
                onChange={() => setUrgentOnly((v) => !v)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  px: 2,
                  borderWidth: 2,
                  borderColor: "warning.main",
                  color: "warning.dark",
                  "&.Mui-selected": {
                    color: "common.white",
                    backgroundColor: "warning.main",
                    "&:hover": { backgroundColor: "warning.dark" },
                  },
                  "&:hover": { backgroundColor: (t) => alpha(t.palette.warning.main, 0.12) },
                }}
              >
                <WarningAmberIcon fontSize="small" sx={{ mr: 0.5 }} /> Urgentes
              </ToggleButton>
            </Tooltip>
            <Tooltip
              title={
                wideScreen
                  ? "Volver al ancho de la página"
                  : "Pantalla ancha: aprovecha todo el ancho y muestra más columnas"
              }
            >
              <IconButton
                size="small"
                color={wideScreen ? "primary" : "default"}
                onClick={toggleWideScreen}
                aria-pressed={wideScreen}
              >
                {wideScreen ? <WidthNormalIcon fontSize="small" /> : <WidthFullIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            {hasColumnWidths && (
              <Tooltip title="Restablecer el ancho de las columnas">
                <IconButton size="small" onClick={resetColumnWidths}>
                  <ViewColumnIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Recargar">
              <span>
                <IconButton
                  size="small"
                  onClick={() => loadPage(true)}
                  disabled={loading || loadingMore}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        <Typography
          variant="caption"
          color={cascading ? "primary" : "text.secondary"}
          sx={{ display: "block", mt: 1 }}
        >
          {statusMessage}
        </Typography>
      </Paper>

      <Paper sx={{ position: "relative", overflow: "hidden" }}>
        {(cascading || loadingMore) && (
          <LinearProgress
            color="primary"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 2,
              height: 6,
              borderTopLeftRadius: (t) => t.shape.borderRadius,
              borderTopRightRadius: (t) => t.shape.borderRadius,
              boxShadow: (t) => `0 2px 8px ${t.palette.primary.main}55`,
              "& .MuiLinearProgress-bar": {
                backgroundImage: (t) =>
                  `linear-gradient(90deg, ${t.palette.primary.light}, ${t.palette.primary.main}, ${t.palette.primary.dark})`,
              },
            }}
          />
        )}
        {loading && !list.length ? (
          <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Only rendered when the table really does overflow — and then at
                the top, because the bottom of a 50-row list is nowhere near the
                columns you are looking for. */}
            <TopScrollbar targetRef={tableScrollRef} watch={filtered.length} />
            <TableContainer ref={tableScrollRef}>
            <Table
              size="small"
              sx={{
                // Left to itself the browser sizes columns from content, which
                // is the right default; a dragged width only sticks once the
                // table stops doing that.
                tableLayout: hasColumnWidths ? "fixed" : "auto",
                "& .MuiTableCell-root": {
                  px: { xs: 0.75, sm: 1, md: 1.25 },
                  py: 0.75,
                },
                // Under a fixed layout every cell has to say what it does with
                // text too long for its column, or the row grows instead.
                ...(hasColumnWidths && {
                  "& .MuiTableCell-root": {
                    px: { xs: 0.75, sm: 1, md: 1.25 },
                    py: 0.75,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }),
              }}
            >
              <TableHead sx={{ "& .MuiTableCell-head": { fontWeight: 700 } }}>
                <TableRow>
                  <HeadCell {...headProps} width={columnWidths.radicado} columnKey="radicado">Radicado</HeadCell>
                  <HeadCell {...headProps} width={columnWidths.subject} columnKey="subject">Asunto</HeadCell>
                  <HeadCell {...headProps} width={columnWidths.type} columnKey="type" sx={COLUMN_AT.type}>Tipo</HeadCell>
                  <HeadCell {...headProps} width={columnWidths.priority} columnKey="priority" sx={COLUMN_AT.priority}>Prioridad</HeadCell>
                  {showDependency && (
                    <HeadCell {...headProps} width={columnWidths.dependency} columnKey="dependency" sx={COLUMN_AT.dependency}>
                      Dependencia
                    </HeadCell>
                  )}
                  <HeadCell {...headProps} width={columnWidths.solicitor} columnKey="solicitor" sx={COLUMN_AT.solicitor}>Solicitante</HeadCell>
                  <HeadCell {...headProps} width={columnWidths.received} columnKey="received" sx={COLUMN_AT.received}>Recibida</HeadCell>
                  <HeadCell {...headProps} width={columnWidths.sla} columnKey="sla" sx={COLUMN_AT.sla}>SLA</HeadCell>
                  <HeadCell {...headProps} width={columnWidths.daysLeft} columnKey="daysLeft">Días</HeadCell>
                  <HeadCell {...headProps} width={columnWidths.status} columnKey="status">Estado</HeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((p) => {
                  const overdue = p.pqrsd?.slaBreached;
                  const isTerminal = TERMINAL_STATUS.has(p.pqrsd?.status);
                  const isPaused = !!p.pqrsd?.slaPaused;
                  const daysLeft = isTerminal || isPaused ? null : calendarDaysLeft(p.pqrsd?.slaDeadline);
                  // Build the days-left chip + decide how loud the whole row is:
                  // red when vencida, amber when due within 3 days, green when
                  // there's comfortable time left.
                  let daysCell = <Typography variant="body2" color="text.secondary">—</Typography>;
                  let rowSeverity = null; // "error" | "warning" | null
                  // A frozen countdown would read as "still ticking"; say why.
                  if (isPaused) {
                    daysCell = <Chip label="Suspendido" size="small" color="secondary" variant="outlined" />;
                  }
                  if (daysLeft != null) {
                    if (overdue || daysLeft < 0) {
                      rowSeverity = "error";
                      daysCell = (
                        <Chip
                          label={daysLeft < 0 ? `Vencida (${Math.abs(daysLeft)} d)` : "Vencida"}
                          color="error"
                          size="small"
                        />
                      );
                    } else if (daysLeft <= 3) {
                      rowSeverity = "warning";
                      daysCell = (
                        <Chip
                          label={daysLeft === 0 ? "Hoy" : `${daysLeft} días`}
                          color="warning"
                          size="small"
                        />
                      );
                    } else {
                      daysCell = <Chip label={`${daysLeft} días`} color="success" size="small" />;
                    }
                  }
                  return (
                    <TableRow
                      key={p.filedId}
                      hover
                      onClick={() => navigate(`/pqrsd/admin/${encodeURIComponent(p.filedId)}`)}
                      sx={{
                        cursor: "pointer",
                        // Loud full-row alarm: red for vencidas, amber for the
                        // ones about to vence. The hover override keeps the tint
                        // visible while still giving feedback on the hovered row.
                        ...(rowSeverity && {
                          backgroundColor: (t) => alpha(t.palette[rowSeverity].main, 0.18),
                          "&:hover": {
                            backgroundColor: (t) => alpha(t.palette[rowSeverity].main, 0.28),
                          },
                        }),
                      }}
                    >
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          whiteSpace: "nowrap",
                          fontSize: { xs: "0.72rem", sm: "0.8125rem" },
                        }}
                      >
                        {p.filedId}
                      </TableCell>
                      {/* The one column allowed to be long, so it is the one
                          that gives way: it truncates instead of pushing the
                          table wider, with the full text on hover. */}
                      <TableCell
                        title={p.subject || ""}
                        sx={
                          columnWidths.subject
                            ? { ...SUBJECT_SX, maxWidth: "none" }
                            : SUBJECT_SX
                        }
                      >
                        {p.subject || "—"}
                      </TableCell>
                      <TableCell sx={COLUMN_AT.type}>
                        {PQRSD_TYPES[p.pqrsd?.type] || p.pqrsd?.type || "—"}
                      </TableCell>
                      <TableCell sx={COLUMN_AT.priority}>
                        {p.pqrsd?.priority ? (
                          <Chip
                            label={PQRSD_PRIORITY_LABELS[p.pqrsd.priority] || p.pqrsd.priority}
                            color={PQRSD_PRIORITY_COLORS[p.pqrsd.priority] || "default"}
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      {showDependency && (
                        <TableCell sx={COLUMN_AT.dependency}>
                          {p.pqrsd?.groupName || (
                            <Typography variant="body2" color="text.secondary">Sin asignar</Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell sx={COLUMN_AT.solicitor}>
                        {p.pqrsd?.anonymous
                          ? <em>Anónimo</em>
                          : `${p.pqrsd?.solicitor?.name || ""} ${p.pqrsd?.solicitor?.surname || ""}`.trim() || "—"}
                      </TableCell>
                      <TableCell sx={COLUMN_AT.received}>
                        {formatDate(p.createdAt)}
                      </TableCell>
                      <TableCell sx={COLUMN_AT.sla}>
                        {formatDate(p.pqrsd?.slaDeadline)}
                        {/* An annulled PQRSD was never a live petition: no term to have missed. */}
                        {overdue && p.pqrsd?.status !== PQRSD_STATUS.ANULADO && (
                          <Chip label="Vencida" color="error" size="small" sx={{ ml: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell>{daysCell}</TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_LABELS[p.pqrsd?.status] || p.pqrsd?.status || "—"}
                          color={STATUS_COLOR[p.pqrsd?.status] || "default"}
                          size="small"
                        />
                        {(() => {
                          const active = Array.isArray(p.pqrsd?.activeResponseFlows)
                            ? p.pqrsd.activeResponseFlows
                            : [];
                          const hidden =
                            p.pqrsd?.status === "respondido" || p.pqrsd?.status === "cerrado";
                          if (hidden) return null;
                          // Multi-flow model: show how many drafts are in flight.
                          if (active.length > 0) {
                            return (
                              <Chip
                                label={
                                  active.length === 1
                                    ? "En aprobación"
                                    : `${active.length} en aprobación`
                                }
                                color="warning"
                                size="small"
                                variant="outlined"
                                sx={{ ml: 0.5 }}
                              />
                            );
                          }
                          // Legacy single-pointer fallback for old PQRSDs.
                          if (!p.pqrsd?.responseFlowId) return null;
                          if (p.pqrsd?.responseFlowStatus === "rejected") {
                            return (
                              <Chip label="Aprobación devuelta" color="error" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                            );
                          }
                          if (p.pqrsd?.responseFlowStatus === "approved") {
                            return (
                              <Chip label="Aprobada" color="success" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                            );
                          }
                          return (
                            <Chip label="En aprobación" color="warning" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={columnCount} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">{pqrsdText("Sin PQRSD para los filtros aplicados.")}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {hasMore && (
                  <TableRow>
                    <TableCell colSpan={columnCount} ref={sentinelRef} sx={{ py: 2, border: 0 }}>
                      {loadingMore && (
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <CircularProgress size={20} />
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </Container>
  );
}
